import { io } from 'socket.io-client';

class SocketService {
  constructor() {
    this.socket = null;
    this.isConnected = false;
    this.listeners = {};
    this.pendingJoins = []; // Queue for room joins while connecting
    this.activeRooms = []; // Track joined rooms for reconnection
  }

  // Initialize socket connection with authentication token
  connect(token = null) {
    if (this.socket && this.isConnected) {
      console.log('🔌 Socket already connected');
      return;
    }

    // Get token from parameter or localStorage
    const authToken = token || localStorage.getItem('token');

    // Connect to the same origin (works with proxy in development)
    const socketUrl = window.location.origin;
    const isElectron = !!(window.electronAPI?.isElectron);
    console.log('🔌 Connecting to Socket.IO at:', socketUrl, 'authenticated:', !!authToken, 'electron:', isElectron);

    // In Electron, use polling first then upgrade to websocket (avoids HTTP/2 WebSocket issues)
    // In browser, try websocket first for lower latency
    const transports = isElectron ? ['polling', 'websocket'] : ['websocket', 'polling'];

    this.socket = io(socketUrl, {
      transports: transports,
      upgrade: true, // Allow upgrade from polling to websocket
      reconnection: true,
      reconnectionAttempts: 10,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      timeout: 20000,
      // CRITICAL: Send auth token for backend to authenticate socket
      auth: {
        token: authToken
      }
    });

    this.socket.on('connect', () => {
      console.log('🔌 Socket connected:', this.socket.id);
      this.isConnected = true;
      this._notifyListeners('connection', true);

      // Process any pending room joins
      this._processPendingJoins();

      // Re-join active rooms on reconnection
      this._rejoinActiveRooms();
    });

    this.socket.on('disconnect', () => {
      console.log('🔌 Socket disconnected');
      this.isConnected = false;
      this._notifyListeners('connection', false);
    });

    this.socket.on('connect_error', (error) => {
      console.error('🔌 Socket connection error:', error);
      this.isConnected = false;
      this._notifyListeners('connection', false);
    });

    // Listen for errors from server (e.g., room join failures)
    this.socket.on('error', (data) => {
      console.error('🔌 Socket error from server:', data);
      this._notifyListeners('socket_error', data);
    });

    // Order events
    this.socket.on('order_update', (data) => {
      console.log('📡 Received order_update:', data);
      this._notifyListeners('order_update', data);
    });

    this.socket.on('new_order', (data) => {
      console.log('📡 Received new_order:', data);
      this._notifyListeners('new_order', data);

      // In Electron, trigger native notification with sound
      if (window.electronAPI?.showNotification) {
        window.electronAPI.showNotification({
          title: '🆕 New Order!',
          body: 'Order #' + (data.orderNumber || data.order_number || data.orderId || 'New') + ' - ₹' + (data.total || data.order?.pricing?.total || '0'),
          playSound: true
        });
      }
    });

    this.socket.on('order_assigned', (data) => {
      console.log('📡 Received order_assigned:', data);
      this._notifyListeners('order_assigned', data);

      // In Electron, trigger native notification with sound
      if (window.electronAPI?.showNotification) {
        window.electronAPI.showNotification({
          title: '🚴 Delivery Assigned',
          body: 'Order #' + (data.orderNumber || data.order_number || data.orderId) + ' - Driver assigned',
          playSound: true
        });
      }
    });

    this.socket.on('order_cancelled', (data) => {
      console.log('❌ Received order_cancelled:', data);
      this._notifyListeners('order_cancelled', data);

      // In Electron, trigger native notification with sound
      if (window.electronAPI?.showNotification) {
        window.electronAPI.showNotification({
          title: '❌ Order Cancelled',
          body: 'Order #' + (data.orderNumber || data.order_number || data.orderId),
          playSound: true
        });
      }
    });

    this.socket.on('auto_accept_changed', (data) => {
      console.log('⚡ Received auto_accept_changed:', data);
      this._notifyListeners('auto_accept_changed', data);
    });

    this.socket.on('delivery_location', (data) => {
      console.log('📍 Received delivery_location:', data);
      this._notifyListeners('delivery_location', data);
    });

    // Admin live map: receives ALL partner location updates
    this.socket.on('partner_location_update', (data) => {
      this._notifyListeners('partner_location_update', data);
    });

    this.socket.on('payment_confirmed', (data) => {
      console.log('💳 Received payment_confirmed:', data);
      this._notifyListeners('payment_confirmed', data);
    });

    this.socket.on('review_received', (data) => {
      console.log('⭐ Received review_received:', data);
      this._notifyListeners('review_received', data);
    });

    // Restaurant status events
    this.socket.on('restaurant_status_changed', (data) => {
      console.log('🏪 Received restaurant_status_changed:', data);
      this._notifyListeners('restaurant_status_changed', data);
    });

    // Admin asked this POS terminal to render an exact print preview
    this.socket.on('request_print_preview', (data) => {
      console.log('🖨️ Received request_print_preview:', data);
      this._notifyListeners('request_print_preview', data);
    });

    // Earnings events
    this.socket.on('earnings_update', (data) => {
      console.log('💰 Received earnings_update:', data);
      this._notifyListeners('earnings_update', data);
    });

    // Payout events
    this.socket.on('payout_created', (data) => {
      console.log('💵 Received payout_created:', data);
      this._notifyListeners('payout_created', data);
    });

    this.socket.on('payout_status_update', (data) => {
      console.log('💵 Received payout_status_update:', data);
      this._notifyListeners('payout_status_update', data);
    });

    this.socket.on('payout_success', (data) => {
      console.log('✅ Received payout_success:', data);
      this._notifyListeners('payout_success', data);
    });

    this.socket.on('payout_failure', (data) => {
      console.log('❌ Received payout_failure:', data);
      this._notifyListeners('payout_failure', data);
    });

    this.socket.on('payout_schedule_notification', (data) => {
      console.log('📅 Received payout_schedule_notification:', data);
      this._notifyListeners('payout_schedule_notification', data);
    });

    // Call events
    this.socket.on('incoming_call', (data) => {
      console.log('📞 Received incoming_call:', data);
      this._notifyListeners('incoming_call', data);
    });

    this.socket.on('call_accepted', (data) => {
      console.log('✅ Received call_accepted:', data);
      this._notifyListeners('call_accepted', data);
    });

    this.socket.on('call_rejected', (data) => {
      console.log('❌ Received call_rejected:', data);
      this._notifyListeners('call_rejected', data);
    });

    this.socket.on('call_ended', (data) => {
      console.log('📴 Received call_ended:', data);
      this._notifyListeners('call_ended', data);
    });

    this.socket.on('call_missed', (data) => {
      console.log('📵 Received call_missed:', data);
      this._notifyListeners('call_missed', data);
    });

    // Admin notification events (from admin panel)
    this.socket.on('admin_notification', (data) => {
      console.log('📢 Received admin_notification:', data);
      this._notifyListeners('admin_notification', data);

      // In Electron, trigger native notification with sound
      if (window.electronAPI?.showNotification) {
        window.electronAPI.showNotification({
          title: data.title || '📢 Admin Notification',
          body: data.message || data.body || 'New notification from admin',
          playSound: true
        });
      }
    });

    this.socket.on('admin_broadcast', (data) => {
      console.log('📢 Received admin_broadcast:', data);
      this._notifyListeners('admin_broadcast', data);

      // In Electron, trigger native notification with sound
      if (window.electronAPI?.showNotification) {
        window.electronAPI.showNotification({
          title: data.title || '📢 Broadcast',
          body: data.message || data.body || 'Broadcast from admin',
          playSound: true
        });
      }
    });
  }

  // Process pending room joins after connection established
  _processPendingJoins() {
    if (this.pendingJoins.length > 0) {
      console.log('🔌 Processing', this.pendingJoins.length, 'pending room joins');
      this.pendingJoins.forEach(({ type, id }) => {
        switch (type) {
          case 'user':
            this._doJoinUserRoom(id);
            break;
          case 'delivery':
            this._doJoinDeliveryRoom(id);
            break;
          case 'restaurant':
            this._doJoinRestaurantRoom(id);
            break;
          case 'order':
            this._doJoinOrderRoom(id);
            break;
        }
      });
      this.pendingJoins = [];
    }
  }

  // Re-join active rooms after reconnection
  _rejoinActiveRooms() {
    if (this.activeRooms.length > 0) {
      console.log('🔌 Re-joining', this.activeRooms.length, 'active rooms after reconnection');
      this.activeRooms.forEach(({ type, id }) => {
        switch (type) {
          case 'user':
            this.socket.emit('join_user', id);
            console.log('👤 Re-joined user room: user_' + id);
            break;
          case 'delivery':
            this.socket.emit('join_delivery', id);
            console.log('🚴 Re-joined delivery room: delivery_' + id);
            break;
          case 'restaurant':
            this.socket.emit('join_restaurant', id);
            console.log('🍕 Re-joined restaurant room: restaurant_' + id);
            break;
          case 'order':
            this.socket.emit('join_order', id);
            console.log('📦 Re-joined order room: order_' + id);
            break;
        }
      });
    }
  }

  // Track active room
  _trackRoom(type, id) {
    // Avoid duplicates
    const exists = this.activeRooms.some(r => r.type === type && r.id === id);
    if (!exists) {
      this.activeRooms.push({ type, id });
    }
  }

  // Internal join methods
  _doJoinUserRoom(userId) {
    this.socket.emit('join_user', userId);
    this._trackRoom('user', userId);
    console.log('👤 Joined user room: user_' + userId);
  }

  _doJoinDeliveryRoom(deliveryPartnerId) {
    this.socket.emit('join_delivery', deliveryPartnerId);
    this._trackRoom('delivery', deliveryPartnerId);
    console.log('🚴 Joined delivery room: delivery_' + deliveryPartnerId);
  }

  _doJoinRestaurantRoom(restaurantId) {
    this.socket.emit('join_restaurant', restaurantId);
    this._trackRoom('restaurant', restaurantId);
    console.log('🍕 Joined restaurant room: restaurant_' + restaurantId);
  }

  _doJoinOrderRoom(orderId) {
    this.socket.emit('join_order', orderId);
    this._trackRoom('order', orderId);
    console.log('📦 Joined order room: order_' + orderId);
  }

  // Join user room (for customers)
  joinUserRoom(userId) {
    if (!this.socket || !this.isConnected) {
      console.log('🔌 Socket not connected yet, queuing user room join:', userId);
      this.pendingJoins.push({ type: 'user', id: userId });
      return;
    }
    this._doJoinUserRoom(userId);
  }

  // Join delivery partner room
  joinDeliveryRoom(deliveryPartnerId) {
    if (!this.socket || !this.isConnected) {
      console.log('🔌 Socket not connected yet, queuing delivery room join:', deliveryPartnerId);
      this.pendingJoins.push({ type: 'delivery', id: deliveryPartnerId });
      return;
    }
    this._doJoinDeliveryRoom(deliveryPartnerId);
  }

  // Join restaurant room
  joinRestaurantRoom(restaurantId) {
    if (!this.socket || !this.isConnected) {
      console.log('🔌 Socket not connected yet, queuing restaurant room join:', restaurantId);
      this.pendingJoins.push({ type: 'restaurant', id: restaurantId });
      return;
    }
    this._doJoinRestaurantRoom(restaurantId);
  }

  // Join specific order room for tracking
  joinOrderRoom(orderId) {
    if (!this.socket || !this.isConnected) {
      console.log('🔌 Socket not connected yet, queuing order room join:', orderId);
      this.pendingJoins.push({ type: 'order', id: orderId });
      return;
    }
    this._doJoinOrderRoom(orderId);
  }

  // Leave order room
  leaveOrderRoom(orderId) {
    if (!this.socket || !this.isConnected) return;
    this.socket.emit('leave_order', orderId);
    console.log('📦 Left order room: order_' + orderId);
  }

  // Send real-time location update (for delivery partners)
  sendLocationUpdate(orderId, lat, lng) {
    if (!this.socket || !this.isConnected) {
      console.log('🔌 Socket not connected, cannot send location');
      return;
    }
    this.socket.emit('location_update', { orderId, lat, lng });
    console.log('📍 Sent location update for order', orderId);
  }

  // Subscribe to events
  on(event, callback) {
    if (!this.listeners[event]) {
      this.listeners[event] = [];
    }
    this.listeners[event].push(callback);

    // Return unsubscribe function
    return () => {
      this.listeners[event] = this.listeners[event].filter(cb => cb !== callback);
    };
  }

  // Notify all listeners
  _notifyListeners(event, data) {
    if (this.listeners[event]) {
      this.listeners[event].forEach(callback => callback(data));
    }
  }

  // Remove all listeners for an event (use on component unmount)
  off(event) {
    if (event) {
      delete this.listeners[event];
    } else {
      this.listeners = {};
    }
  }

  // Emit an event to server
  emit(event, data) {
    if (!this.socket || !this.isConnected) {
      console.log('🔌 Socket not connected, cannot emit', event);
      return false;
    }
    this.socket.emit(event, data);
    console.log('📤 Emitted', event, data);
    return true;
  }

  // Disconnect socket
  disconnect() {
    if (this.socket) {
      console.log('🔌 Disconnecting socket...');
      this.socket.disconnect();
      this.socket = null;
      this.isConnected = false;
    }
  }

  // Reconnect socket with fresh token (call after login)
  reconnect() {
    console.log('🔌 Reconnecting socket with fresh token...');
    this.disconnect();
    // Small delay to ensure clean disconnect
    setTimeout(() => {
      this.connect();
    }, 100);
  }
}

// Singleton instance
const socketService = new SocketService();

// Expose on window for Electron's injected script to access
if (typeof window !== 'undefined') {
  window.socketService = socketService;
}

export default socketService;
